import { createAsyncThunk } from '@reduxjs/toolkit'
import type { RootState } from 'store'
import { DEFAULT_PRODUCTS, MINIMUM_INSTALMENT } from 'constants/customer-flow'
import { mapSimulatePriceRequest } from 'features/simulation/utils'
import type { Proposition, SimulatedPropositions } from 'store/customer/types'
import { addLogBreadcrumb, log, setLogContext } from 'utils/logging'
import { Criticality, Flow, Severity } from 'types/logging'
import axios from 'axios'
import { NextApiResponseBody } from 'types/bolt-api/request'
import { SimulatePriceResponse } from 'features/simulation/types'
import { Product } from '@boltenergy-be/shared'
import { InputGroupKeys } from 'store/customer/enums'
import { CLIENT_CONTEXT_HEADER } from 'constants/logging'
import { generateRequestId } from 'utils/helpers'

export const runSimulation = createAsyncThunk<SimulatedPropositions, { products?: Product[] }>(
  'customer/runSimulation',
  async ({ products }, thunkAPI) => {
    try {
      // Fetch current propositions from the store
      const state = thunkAPI.getState() as RootState
      const { propositions, inputs } = state.customer
      const tempPropositions = { ...propositions?.simulatedPropositions }
      const { needsGas } = inputs[InputGroupKeys.DELIVERY_ADDRESS]

      const previouslySimulatedProducts =
        tempPropositions && Object.keys(tempPropositions).length ? (Object.keys(tempPropositions) as Product[]) : undefined
      const productsToSimulate = products || previouslySimulatedProducts || DEFAULT_PRODUCTS
      const doubleEnergyMaxInstalment = needsGas ? MINIMUM_INSTALMENT / 2 : MINIMUM_INSTALMENT

      addLogBreadcrumb('runSimulation: started', { productsToSimulate })

      if (!productsToSimulate.length) {
        log({
          error: 'No products to simulate',
          identifier: `[${Flow.SIMULATION}:runSimulation]`,
          severity: Severity.WARNING,
          criticality: Criticality.LOW,
          metaData: {
            products_to_simulate: {
              availableProducts: state.marketing.availableProducts,
              region: inputs[InputGroupKeys.SIMULATION].region,
              requestedProducts: products
            }
          }
        })

        return thunkAPI.rejectWithValue('[runSimulation]: No products to simulate')
      }

      // Fetch propositions for all the products
      await Promise.all(
        productsToSimulate.map(async (product) => {
          // Map the request data
          const simulatePriceRequest = mapSimulatePriceRequest(product)
          const requestId = generateRequestId()

          setLogContext('simulation_price_request_body', simulatePriceRequest)

          const requestConfig = {
            headers: {
              // The API route logs this alongside its own error, which is what ties both events together.
              [CLIENT_CONTEXT_HEADER]: encodeURIComponent(
                JSON.stringify({
                  requestId,
                  opportunityId: state.app.opportunityId,
                  product,
                  region: inputs[InputGroupKeys.SIMULATION].region,
                  sourceType: state.marketing.sourceType,
                  usageGrade: inputs[InputGroupKeys.SIMULATION].usage?.grade
                })
              )
            },
            // Resolve instead of throwing, so one failing product neither aborts the other simulations nor skips
            // the log below, which is the only place that still knows which product & payload failed.
            validateStatus: () => true
          }

          // Fetch the price(s)
          const [priceResponse, priceWithoutPromoResponse] = await Promise.all([
            axios.post<NextApiResponseBody<SimulatePriceResponse>>('/api/website/price-simulation', simulatePriceRequest, requestConfig),
            simulatePriceRequest.discountCode
              ? axios.post<NextApiResponseBody<SimulatePriceResponse>>(
                  '/api/website/price-simulation',
                  {
                    ...simulatePriceRequest,
                    discountCode: undefined
                  },
                  requestConfig
                )
              : undefined
          ])

          setLogContext('price_simulation_response', {
            [product]: {
              success: priceResponse?.data?.success,
              price: priceResponse?.data?.success ? priceResponse?.data.data : priceResponse?.data?.message,
              priceWithoutPromo: priceWithoutPromoResponse?.data?.success
                ? priceWithoutPromoResponse?.data?.data
                : priceWithoutPromoResponse?.data?.message
            }
          })
          addLogBreadcrumb('runSimulation: product simulated', { product, success: priceResponse?.data?.success })

          // TODO: not sure if this is the way to go, if a simulation returns no price it probably has failed the request.
          //  Thus we should not be able to proceed with the simulation/registration
          if (!priceResponse?.data?.success || !priceResponse.data.data) {
            log({
              error: 'Price simulation failed',
              identifier: `[${Flow.SIMULATION}:runSimulation]`,
              tags: { request_id: requestId },
              metaData: {
                failed_simulation: {
                  message: priceResponse?.data?.message,
                  product,
                  request: simulatePriceRequest,
                  requestId,
                  status: priceResponse?.status
                }
              }
            })
            return
          }

          const { price } = priceResponse.data.data

          // Minimum instalment is the same for residential & professional clients (incl vs excl btw) and will be sent to Haugazel.
          // This decision was made because it will be the absolute minimum and calculating the VAT will get too complicated because of different VAT percentages.
          const minimalInstalmentUsed: Omit<Proposition['minimalInstalmentUsed'], 'total'> = {
            electricity: price.electricity.total < doubleEnergyMaxInstalment,
            gas: needsGas && price.gas ? price.gas.total < doubleEnergyMaxInstalment : false
          }

          // Set/replace the proposition for the current product type
          tempPropositions[product] = {
            price: {
              total: Object.values(minimalInstalmentUsed).some(Boolean)
                ? {
                    ...price.total,
                    total:
                      needsGas && price.gas
                        ? Math.max(price.electricity.total, doubleEnergyMaxInstalment) +
                          Math.max(price.gas.total, doubleEnergyMaxInstalment)
                        : MINIMUM_INSTALMENT,
                    totalWithoutVAT:
                      needsGas && price.gas
                        ? Math.max(price.electricity.totalWithoutVAT, doubleEnergyMaxInstalment) +
                          Math.max(price.gas!.totalWithoutVAT, doubleEnergyMaxInstalment)
                        : MINIMUM_INSTALMENT
                  }
                : price.total,
              electricity: minimalInstalmentUsed.electricity
                ? {
                    ...price.electricity,
                    total: doubleEnergyMaxInstalment,
                    totalWithoutVAT: doubleEnergyMaxInstalment
                  }
                : price.electricity,
              gas:
                needsGas && price.gas
                  ? {
                      ...price.gas,
                      total: minimalInstalmentUsed.gas ? doubleEnergyMaxInstalment : price.gas.total,
                      totalWithoutVAT: minimalInstalmentUsed.gas ? doubleEnergyMaxInstalment : price.gas.totalWithoutVAT
                    }
                  : undefined
            },
            priceWithoutPromo: priceWithoutPromoResponse?.data?.data?.price?.total
              ? Math.max(
                  priceWithoutPromoResponse.data.data.price.total?.[simulatePriceRequest.isCompany ? 'totalWithoutVAT' : 'total'],
                  MINIMUM_INSTALMENT
                )
              : undefined,
            minimalInstalmentUsed
          }
        })
      )

      if (!Object.keys(tempPropositions).length) {
        log({ error: 'Failed to fetch propositions', identifier: `[${Flow.SIMULATION}:runSimulation]` })
        return thunkAPI.rejectWithValue('[runSimulation]: Failed to fetch the propositions')
      }

      return tempPropositions
    } catch (error) {
      addLogBreadcrumb('runSimulation: failed')
      log({ error, identifier: `[${Flow.SIMULATION}:runSimulation]` })
      return thunkAPI.rejectWithValue('[runSimulation]: Internal error')
    }
  }
)
